import { useState } from 'react';
import PropTypes from 'prop-types';
import { EventBus } from 'use-event-bus-hook';
// Components
import Entity from '../../entity/Entity';
import Icon from '../../icon/Icon';
import TooltipHelper from '@/common-utils/components/renders/tooltip/TooltipHelper';
// Common
import ICONS from '@/common-utils/enums/icons';
import TABLE_CONSTANTS from '../enums/Constants';
import DATA_TABLE_EVENT_BUS_NAMES from '../enums/DataTableEventBusNames';
import createDataCyAttribute from '@/common-utils/functions/createDataCyAttribute';
// Styles
import './Header.scss';
/**
 * @param root0
 * @param root0.column
 * @param root0.query
 * @param root0.setQuery
 * @param root0.queryStored
 * @param root0.storeQuery
 * @param root0.setLaunchRefetch
 * @param root0.tooltip
 * @param root0.namespace
 * @param root0.filtersEventName
 * @returns {JSX} Header
 */
function Header({ column, query, setQuery, queryStored, storeQuery = () => { }, setLaunchRefetch, tooltip, namespace, filtersEventName }) {

    const hiddenFilterKeys = new Set((query.filters ?? []).filter(f => f.hidden).map(f => f.key));
    const [queryOrders] = useState(queryStored.orders.length > 0 ? queryStored.orders : query.orders);
    const setNewOrderIcon = (orders) => {
        const indexOrderToUpdate = orders.findIndex(order => order.key === column.id);
        if (indexOrderToUpdate === -1) {
            return 2;
        } else {
            // order is existing
            const oldOrderToUpdate = orders[indexOrderToUpdate];
            if (oldOrderToUpdate.desc) {
                // order is already set to desc : we need to remove it
                return 1;
            } else {
                // order is asc : we can set it to desc
                return 0;
            }
        }
    };
    const [orderIcon] = useState(setNewOrderIcon(query.orders));

    const updateQueryOrders = () => {
        // enableSorting: true
        if (column.getCanSort()) {
            const indexOrderToUpdate = queryOrders.findIndex(order => order.key === column.id);
            let updatedOrder = {};
            if (indexOrderToUpdate === -1) {
                // order not existing : append it to the orders list, asc by default
                updatedOrder = [...queryOrders, { key: column.id, desc: false }];
            } else {
                // order is existing
                const oldOrderToUpdate = queryOrders[indexOrderToUpdate];
                if (oldOrderToUpdate.desc) {
                    // order is already set to desc : we need to remove it
                    updatedOrder = [...queryOrders.slice(0, indexOrderToUpdate), ...queryOrders.slice(indexOrderToUpdate + 1)];
                } else {
                    // order is asc : we can set it to desc
                    updatedOrder = [...queryOrders.slice(0, indexOrderToUpdate), { key: column.id, desc: true }, ...queryOrders.slice(indexOrderToUpdate + 1)];
                }
            }
            setQuery({
                offset: TABLE_CONSTANTS.OFFSET_STARTING_VALUE,
                limit: TABLE_CONSTANTS.ROWS_NUMBER_PER_PAGE,
                orders: updatedOrder,
                filters: query.filters
            });
            storeQuery({
                offset: TABLE_CONSTANTS.OFFSET_STARTING_VALUE,
                limit: TABLE_CONSTANTS.ROWS_NUMBER_PER_PAGE,
                orders: updatedOrder,
                filters: query.filters
            });
            setLaunchRefetch(true);
        }
    };

    return (
        <button
            className='rt-table-cust-header text-labelGray-foreground bg-transparent border-none p-0 m-0 cursor-pointer space-x-1'
        >
            <Entity
                nowrap
                entity={column.columnDef.entity}
                namespace={column.columnDef.namespace}
                icon={column.columnDef.filter && !hiddenFilterKeys.has(column.id) ? ICONS.FILTER : undefined}
                onClick={() => {
                    if (column.columnDef.filter && !hiddenFilterKeys.has(column.id)) {
                        EventBus.emit(DATA_TABLE_EVENT_BUS_NAMES.OPEN_FILTER, { ...column.columnDef, filterEventName: filtersEventName });
                    }
                }}
                alternativeDataCy={createDataCyAttribute(column.id)}
                iconSize={16}
            />
            {
                tooltip && namespace &&
                <TooltipHelper
                    trigger={<Icon icon={ICONS.HELP} />}
                    content={<Entity
                        entity={tooltip}
                        namespace={namespace}
                    />}
                    contentClass={'max-w-80 text-center'}
                />
            }
            {
                column.getCanSort() &&
                <span className='sort-icons cursor-pointer' onClick={updateQueryOrders}>
                    {
                        orderIcon === 0 &&
                        <Icon icon={ICONS.CHEVRON_UP} strokeWidth={3} size={16} />
                    }
                    {
                        orderIcon === 1 &&
                        <Icon icon={ICONS.CHEVRON_DOWN} strokeWidth={3} size={16} />
                    }
                    {
                        orderIcon === 2 &&
                        <span className='not-sorted space-y-[10px]'>
                            <Icon icon={ICONS.CHEVRON_UP} className={'order-icon-height'} strokeWidth={3} size={16} />
                            <Icon className='icon-down' icon={ICONS.CHEVRON_DOWN} strokeWidth={3} size={16} />
                        </span>
                    }
                </span>
            }
        </button >
    );
}

Header.propTypes = {
    column: PropTypes.object.isRequired,
    query: PropTypes.object.isRequired,
    setQuery: PropTypes.func.isRequired,
    setLaunchRefetch: PropTypes.func.isRequired,
    queryStored: PropTypes.object,
    storeQuery: PropTypes.func,
    tooltip: PropTypes.string,
    namespace: PropTypes.string,
    filtersEventName: PropTypes.string
};
export default Header;